// Какво се сравнява в „История на промените" на всеки от четирите документа:
// полетата на заглавката, редовете (ключ за сдвояване, основна стойност,
// подробности) и общите суми за контекст. Само въведеното от човек — вижте
// бележката в `revisionDiffModel.ts` защо FIFO цената, очакваното и „по
// фактура" НЕ са тук.
//
// Пари с ТОЧКА (`formatMoney`), ед. цена до 5 знака (`formatUnitPrice`),
// количества със ЗАПЕТАЯ и знаците на мерната единица (`formatQty`).
import type {
  Delivery,
  DeliveryLine,
  Stocktake,
  StocktakeLine,
  Transfer,
  Writeoff,
  WriteoffLine,
} from "../../../../api/inventory";
import type { RevisionSnapshot } from "../../../../api/inventoryRevisions";
import { fmtDate } from "../dateTime";
import { formatMoney, formatUnitPrice, UNIT_PRICE_DECIMALS } from "../numbers";
import { formatQty } from "../qty";
import type {
  DocumentSpec,
  HeaderFieldSpec,
  LineDetail,
  LineItem,
  RevisionDiffContext,
  RevisionDocumentKind,
} from "./revisionDiffModel";

// ─── Нормализиране ────────────────────────────────────────────────────────────
const text = (value: unknown): string | null => {
  const trimmed = value == null ? "" : String(value).trim();
  return trimmed || null;
};
const fixed = (value: unknown, digits: number): string => {
  const number = Number(value);
  return value == null || !Number.isFinite(number) ? "" : number.toFixed(digits);
};
const day = (iso: unknown): string | null => (typeof iso === "string" && iso ? fmtDate(iso) : null);
const idKey = (value: unknown): string => (value == null ? "" : String(value));

const qtyText = (value: unknown, productId: number, ctx: RevisionDiffContext): string => {
  const unit = ctx.unitOf(productId);
  const quantity = formatQty(Number(value ?? 0), ctx.decimalsOf(productId));
  return unit ? `${quantity} ${unit}` : quantity;
};

// Порции (множител на рецепта) — без излишни нули: ×3, а не ×3,000
const portionsText = (value: number) =>
  `×${value.toLocaleString("bg-BG", { maximumFractionDigits: 3 })}`;

// ─── Общи полета на заглавката ────────────────────────────────────────────────
const dateField: HeaderFieldSpec = {
  field: "date",
  label: (ctx) => ctx.t("inventoryPage.colDate"),
  key: (snapshot) => day((snapshot as { date?: string }).date) ?? "",
  display: (snapshot) => day((snapshot as { date?: string }).date),
};

const textField = (
  field: string,
  labelKey: string,
  read: (snapshot: RevisionSnapshot) => unknown,
): HeaderFieldSpec => ({
  field,
  label: (ctx) => ctx.t(labelKey),
  key: (snapshot) => text(read(snapshot)) ?? "",
  display: (snapshot) => text(read(snapshot)),
});

// Поле по id (доставчик, склад) — сравнява се id-то, показва се името
const idField = (
  field: string,
  labelKey: string,
  readId: (snapshot: RevisionSnapshot) => unknown,
  readName: (snapshot: RevisionSnapshot) => unknown,
): HeaderFieldSpec => ({
  field,
  label: (ctx) => ctx.t(labelKey),
  key: (snapshot) => idKey(readId(snapshot)),
  display: (snapshot) =>
    text(readName(snapshot)) ?? (readId(snapshot) != null ? `#${readId(snapshot)}` : null),
});

const detail = (field: string, label: string, key: string, display: string | null): LineDetail => ({
  field,
  label,
  key,
  display,
});

// ─── Доставка ─────────────────────────────────────────────────────────────────
const asDelivery = (snapshot: RevisionSnapshot) => snapshot as Partial<Delivery>;
const docTypeOf = (snapshot: RevisionSnapshot) =>
  asDelivery(snapshot).documentType === "note" ? "note" : "invoice";

const paymentKey = (snapshot: RevisionSnapshot) => {
  const delivery = asDelivery(snapshot);
  if (delivery.paymentStatus !== "paid") return delivery.paymentStatus ?? "";
  return ["paid", day(delivery.paidAt), delivery.paymentMethod, text(delivery.paymentReference)].join("|");
};

const paymentText = (snapshot: RevisionSnapshot, ctx: RevisionDiffContext): string | null => {
  const delivery = asDelivery(snapshot);
  switch (delivery.paymentStatus) {
    case "paid":
      return [
        ctx.t("inventoryPage.payPaid"),
        day(delivery.paidAt),
        delivery.paymentMethod ? ctx.t(`inventoryPage.payMethod_${delivery.paymentMethod}`) : null,
        text(delivery.paymentReference),
      ]
        .filter(Boolean)
        .join(" · ");
    case "unpaid":
      return ctx.t("inventoryPage.payUnpaid");
    case "by_invoice":
      return ctx.t("inventoryPage.payByInvoice");
    default:
      return null; // заварена доставка — плащането ѝ не се следи
  }
};

const sortedIds = (ids: unknown) =>
  (Array.isArray(ids) ? ids.map(Number) : []).sort((left, right) => left - right);

const deliveryHeader: HeaderFieldSpec[] = [
  dateField,
  {
    field: "documentType",
    label: (ctx) => ctx.t("inventoryPage.colDocType"),
    key: docTypeOf,
    display: (snapshot, ctx) =>
      ctx.t(docTypeOf(snapshot) === "note" ? "inventoryPage.docTypeNote" : "inventoryPage.docTypeInvoice"),
  },
  {
    field: "invoiceNumber",
    label: (ctx, _before, after) =>
      ctx.t(docTypeOf(after) === "note" ? "inventoryPage.colNoteNo" : "inventoryPage.colInvoice"),
    key: (snapshot) => text(asDelivery(snapshot).invoiceNumber) ?? "",
    display: (snapshot, ctx) =>
      text(asDelivery(snapshot).invoiceNumber) ?? ctx.t("inventoryPage.noDocNumber"),
  },
  idField(
    "supplier",
    "inventoryPage.colSupplier",
    (snapshot) => asDelivery(snapshot).supplierId,
    (snapshot) => asDelivery(snapshot).supplierName,
  ),
  {
    field: "linkedNotes",
    label: (ctx) => ctx.t("inventoryPage.revLinkedNotes"),
    key: (snapshot) => sortedIds(asDelivery(snapshot).linkedNoteIds).join(","),
    display: (snapshot, ctx) =>
      sortedIds(asDelivery(snapshot).linkedNoteIds)
        .map((id) => ctx.t("inventoryPage.internalNo", { id }))
        .join(", ") || null,
  },
  textField("note", "inventoryPage.fieldNote", (snapshot) => asDelivery(snapshot).note),
  {
    field: "payment",
    label: (ctx) => ctx.t("inventoryPage.colPayment"),
    // стоковата във фактура се плаща ПРЕЗ фактурата — промяната е там
    skip: (before, after) =>
      asDelivery(before).paymentStatus === "by_invoice" ||
      asDelivery(after).paymentStatus === "by_invoice",
    key: paymentKey,
    display: paymentText,
  },
  // стоковата: във фактура (изваждане/закачане) и приключена без фактура
  {
    field: "invoiceLink",
    label: (ctx) => ctx.t("inventoryPage.colLinks"),
    skip: (before, after) => docTypeOf(before) !== "note" && docTypeOf(after) !== "note",
    key: (snapshot) => idKey(asDelivery(snapshot).invoiceId),
    display: (snapshot, ctx) => {
      const invoiceId = asDelivery(snapshot).invoiceId;
      return invoiceId != null ? ctx.t("inventoryPage.linkInvoice", { id: invoiceId }) : null;
    },
  },
  {
    field: "closedWithoutInvoice",
    label: (ctx) => ctx.t("inventoryPage.revClosedWithoutInvoice"),
    key: (snapshot) =>
      [day(asDelivery(snapshot).closedWithoutInvoiceAt), text(asDelivery(snapshot).closedWithoutInvoiceReason)]
        .filter(Boolean)
        .join("|"),
    display: (snapshot) =>
      [day(asDelivery(snapshot).closedWithoutInvoiceAt), text(asDelivery(snapshot).closedWithoutInvoiceReason)]
        .filter(Boolean)
        .join(" · ") || null,
  },
];

const deliveryTotals: HeaderFieldSpec[] = [
  {
    field: "totalNet",
    label: (ctx) => ctx.t("inventoryPage.revTotalNet"),
    key: (snapshot) => fixed(asDelivery(snapshot).totalValueNoVat ?? 0, 2),
    display: (snapshot) => formatMoney(Number(asDelivery(snapshot).totalValueNoVat ?? 0)),
  },
  {
    field: "totalGross",
    label: (ctx) => ctx.t("inventoryPage.revTotalGross"),
    key: (snapshot) => fixed(asDelivery(snapshot).totalValueWithVat ?? 0, 2),
    display: (snapshot) => formatMoney(Number(asDelivery(snapshot).totalValueWithVat ?? 0)),
  },
];

const deliveryLines: DocumentSpec["lines"] = (snapshot, ctx, pair) => {
  // складът се показва, само когато доставката е разпределена в повече от един
  const warehouses = new Set(
    [pair.before, pair.after].flatMap((state) =>
      (asDelivery(state).lines ?? []).map((line) => line.warehouseId),
    ),
  );
  return (asDelivery(snapshot).lines ?? []).map((line: DeliveryLine): LineItem => {
    const packaging = line.packagingName
      ? `${line.packagingName} × ${Number(line.packagingsCount ?? 0).toLocaleString("bg-BG", {
          maximumFractionDigits: 3,
        })}`
      : null;
    return {
      key: `${line.productId}|${line.warehouseId}`,
      exactKey: `${line.productId}|${line.warehouseId}|${text(line.batchNumber) ?? ""}`,
      name: line.productName,
      context: warehouses.size > 1 ? line.warehouseName : null,
      mainLabel: null,
      summary: `${qtyText(line.quantity, line.productId, ctx)} × ${formatUnitPrice(line.unitPrice)}`,
      mainKey: `${fixed(line.quantity, 4)}|${fixed(line.unitPrice, UNIT_PRICE_DECIMALS)}`,
      details: [
        detail(
          "packaging",
          ctx.t("inventoryPage.colPackaging"),
          `${idKey(line.packagingId)}|${fixed(line.packagingsCount, 4)}`,
          packaging,
        ),
        detail("batch", ctx.t("inventoryPage.colBatch"), text(line.batchNumber) ?? "", text(line.batchNumber)),
        detail("expiry", ctx.t("inventoryPage.colExpiry"), day(line.expiryDate) ?? "", day(line.expiryDate)),
        detail("note", ctx.t("inventoryPage.fieldNote"), text(line.note) ?? "", text(line.note)),
        detail("net", ctx.t("inventoryPage.colNet"), fixed(line.sumNoVat, 2), formatMoney(line.sumNoVat)),
        detail("gross", ctx.t("inventoryPage.colGross"), fixed(line.sumWithVat, 2), formatMoney(line.sumWithVat)),
      ],
    };
  });
};

// ─── Изписване / бракуване ────────────────────────────────────────────────────
const asWriteoff = (snapshot: RevisionSnapshot) => snapshot as Partial<Writeoff>;

const writeoffHeader: HeaderFieldSpec[] = [
  dateField,
  {
    field: "kind",
    label: (ctx) => ctx.t("inventoryPage.colType"),
    key: (snapshot) => (asWriteoff(snapshot).kind === "scrap" ? "scrap" : "writeoff"),
    display: (snapshot, ctx) =>
      ctx.t(asWriteoff(snapshot).kind === "scrap" ? "inventoryPage.deletedType_scrap" : "inventoryPage.deletedType_writeoff"),
  },
  idField(
    "warehouse",
    "inventoryPage.colWarehouse",
    (snapshot) => asWriteoff(snapshot).warehouseId,
    (snapshot) => asWriteoff(snapshot).warehouseName,
  ),
  textField("reason", "inventoryPage.fieldReason", (snapshot) => asWriteoff(snapshot).reason),
  textField("comment", "inventoryPage.fieldComment", (snapshot) => asWriteoff(snapshot).comment),
];

const writeoffProductItem = (line: WriteoffLine, ctx: RevisionDiffContext): LineItem => {
  const batch = text(line.batchNumber);
  return {
    key: `p|${line.productId}`,
    exactKey: `p|${line.productId}|${batch ?? ""}`,
    name: line.productName,
    context: null,
    mainLabel: null,
    summary: qtyText(line.quantity, line.productId, ctx),
    mainKey: fixed(line.quantity, 4),
    details: [
      detail("batch", ctx.t("inventoryPage.colBatch"), batch ?? "", batch ?? ctx.t("inventoryPage.fifo")),
      // цената е въведена само когато е ръчна; иначе е FIFO и се мени с replay-а
      detail(
        "price",
        ctx.t("inventoryPage.colUnitPrice"),
        line.priceManual ? fixed(line.unitPrice, UNIT_PRICE_DECIMALS) : "fifo",
        line.priceManual ? formatUnitPrice(line.unitPrice) : ctx.t("inventoryPage.fifo"),
      ),
      detail("note", ctx.t("inventoryPage.fieldNote"), text(line.note) ?? "", text(line.note)),
    ],
  };
};

// Рецептата е ЕДИН ред в историята: порциите, не листовите ѝ продукти. Всеки
// рецептен ред на входа дава по един ред на листов продукт със същите порции,
// затова порциите = сборът на `recipeQuantity` по първия ѝ продукт.
const writeoffRecipeItem = (lines: WriteoffLine[], ctx: RevisionDiffContext): LineItem => {
  const first = lines[0];
  const portions = lines
    .filter((line) => line.productId === first.productId)
    .reduce((sum, line) => sum + Number(line.recipeQuantity ?? 0), 0);
  return {
    key: `r|${first.recipeId}`,
    exactKey: `r|${first.recipeId}`,
    name: first.recipeName || `#${first.recipeId}`,
    context: ctx.t("inventoryPage.recipe"),
    mainLabel: null,
    summary: portionsText(portions),
    mainKey: fixed(portions, 4),
    details: [detail("note", ctx.t("inventoryPage.fieldNote"), text(first.note) ?? "", text(first.note))],
  };
};

const writeoffLines: DocumentSpec["lines"] = (snapshot, ctx) => {
  const lines = asWriteoff(snapshot).lines ?? [];
  const items: LineItem[] = [];
  const seenRecipes = new Set<number>();
  for (const line of lines) {
    if (line.recipeId == null) {
      items.push(writeoffProductItem(line, ctx));
    } else if (!seenRecipes.has(line.recipeId)) {
      seenRecipes.add(line.recipeId);
      items.push(writeoffRecipeItem(lines.filter((other) => other.recipeId === line.recipeId), ctx));
    }
  }
  return items;
};

// ─── Прехвърляне ──────────────────────────────────────────────────────────────
const asTransfer = (snapshot: RevisionSnapshot) => snapshot as Partial<Transfer>;

const transferHeader: HeaderFieldSpec[] = [
  dateField,
  idField(
    "fromWarehouse",
    "inventoryPage.colFromWarehouse",
    (snapshot) => asTransfer(snapshot).fromWarehouseId,
    (snapshot) => asTransfer(snapshot).fromWarehouseName,
  ),
  idField(
    "toWarehouse",
    "inventoryPage.colToWarehouse",
    (snapshot) => asTransfer(snapshot).toWarehouseId,
    (snapshot) => asTransfer(snapshot).toWarehouseName,
  ),
  textField("note", "inventoryPage.fieldNote", (snapshot) => asTransfer(snapshot).note),
];

// Един продукт = един ред (две партиди на един продукт се сумират); цената е
// FIFO себестойност на източника и не се сравнява.
const transferLines: DocumentSpec["lines"] = (snapshot, ctx) => {
  const byProduct = new Map<number, { name: string; quantity: number }>();
  for (const line of asTransfer(snapshot).lines ?? []) {
    const current = byProduct.get(line.productId);
    byProduct.set(line.productId, {
      name: line.productName,
      quantity: (current?.quantity ?? 0) + Number(line.quantity ?? 0),
    });
  }
  return [...byProduct.entries()].map(([productId, { name, quantity }]) => ({
    key: `p|${productId}`,
    exactKey: `p|${productId}`,
    name,
    context: null,
    mainLabel: null,
    summary: qtyText(quantity, productId, ctx),
    mainKey: fixed(quantity, 4),
    details: [],
  }));
};

// ─── Инвентаризация ───────────────────────────────────────────────────────────
const asStocktake = (snapshot: RevisionSnapshot) => snapshot as Partial<Stocktake>;

const stocktakeHeader: HeaderFieldSpec[] = [
  dateField,
  idField(
    "warehouse",
    "inventoryPage.colWarehouse",
    (snapshot) => asStocktake(snapshot).warehouseId,
    (snapshot) => asStocktake(snapshot).warehouseName,
  ),
  textField("reason", "inventoryPage.fieldReason", (snapshot) => asStocktake(snapshot).reason),
];

// Сравнява се преброеното („Намерено"), не разликата: очакваното се мени със
// всяка операция със задна дата. „Намерено" е НАСИПНОТО (въведеното в полето)
// — частта в приготвените рецепти (`inRecipes`) се извежда от порциите, които
// се сравняват отделно като ред на рецептата.
const stocktakeLineItem = (line: StocktakeLine, ctx: RevisionDiffContext): LineItem => {
  const legacy = line.kind === "substitute"; // заварен заместващ ред
  const counted = legacy
    ? Math.abs(Number(line.diff ?? 0))
    : line.counted == null
      ? line.counted
      : Math.max(0, line.counted - (line.inRecipes ?? 0));
  return {
    key: `${legacy ? "x" : "p"}|${line.productId}`,
    exactKey: `${legacy ? "x" : "p"}|${line.productId}`,
    name: line.productName,
    context: null,
    mainLabel: legacy ? null : ctx.t("inventoryPage.colCounted"),
    summary: counted == null ? null : qtyText(counted, line.productId, ctx),
    mainKey: fixed(counted, 4),
    details: [
      // ръчна цена; иначе последната доставна (излишък) / FIFO (липса) — не се сравнява
      detail(
        "price",
        ctx.t("inventoryPage.colUnitPrice"),
        line.priceManual ? fixed(line.unitPrice, UNIT_PRICE_DECIMALS) : "auto",
        line.priceManual ? formatUnitPrice(line.unitPrice) : ctx.t("inventoryPage.revAutoPrice"),
      ),
      detail("batch", ctx.t("inventoryPage.colBatch"), text(line.batchNumber) ?? "", text(line.batchNumber)),
      detail("expiry", ctx.t("inventoryPage.colExpiry"), day(line.expiryDate) ?? "", day(line.expiryDate)),
    ],
  };
};

const stocktakeLines: DocumentSpec["lines"] = (snapshot, ctx, pair) => {
  // замените носят само id — името е от редовете на двете състояния или от справочника
  const names = new Map<number, string>();
  for (const state of [pair.before, pair.after]) {
    for (const line of asStocktake(state).lines ?? []) names.set(line.productId, line.productName);
  }
  const nameOf = (productId: number) =>
    names.get(productId) ?? ctx.productNameOf?.(productId) ?? `#${productId}`;
  const stocktake = asStocktake(snapshot);
  return [
    ...(stocktake.lines ?? []).map((line) => stocktakeLineItem(line, ctx)),
    ...(stocktake.substitutions ?? []).map((substitution): LineItem => {
      const key = `s|${substitution.recipientProductId}|${substitution.donorProductId}`;
      return {
        key,
        exactKey: key,
        name: `${nameOf(substitution.donorProductId)} → ${nameOf(substitution.recipientProductId)}`,
        context: ctx.t("inventoryPage.revSubstitution"),
        mainLabel: null,
        summary: qtyText(substitution.recipientQuantity, substitution.recipientProductId, ctx),
        mainKey: fixed(substitution.recipientQuantity, 4),
        details: [],
      };
    }),
    // приготвените рецепти — сравняват се порциите (снимката на съставките не
    // е въведена от човек)
    ...(stocktake.recipes ?? []).map((recipe): LineItem => {
      const key = `r|${recipe.recipeId}`;
      return {
        key,
        exactKey: key,
        name: recipe.recipeName,
        context: ctx.t("inventoryPage.recipe"),
        mainLabel: ctx.t("inventoryPage.portionsPh"),
        summary: portionsText(recipe.portions),
        mainKey: fixed(recipe.portions, 4),
        details: [],
      };
    }),
  ];
};

export const DOCUMENT_SPECS: Record<RevisionDocumentKind, DocumentSpec> = {
  delivery: { header: deliveryHeader, totals: deliveryTotals, lines: deliveryLines },
  writeoff: { header: writeoffHeader, totals: [], lines: writeoffLines },
  transfer: { header: transferHeader, totals: [], lines: transferLines },
  stocktake: { header: stocktakeHeader, totals: [], lines: stocktakeLines },
};
